<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, provide, watchEffect } from 'vue';
import { useResizeObserver } from '@vueuse/core';
import OverflowTooltip from '@/components/OverflowTooltip.vue';
import PeekHeader from '@/components/PeekHeader.vue';
import { useIsPhone } from '@/composables/useIsPhone';
import { useInspectorStore } from '@/stores/inspectorStore';
import { INSPECTOR_HOST } from '@/composables/inspectorHostContext';

const inspectorStore = useInspectorStore();
// A phone shows the peek as a bottom sheet instead (InspectorSheet).
const isPhone = useIsPhone();

// Views rendered in this overlay drill down with push() so Back works (see key docs).
provide(INSPECTOR_HOST, true);

const panelRef = ref<HTMLElement | null>(null);

const STORAGE_KEY = 'inspector-panel-width';
const DEFAULT_WIDTH = 450;
const MIN_WIDTH = 300;
// The panel floats: it keeps this much page visible on every side instead of
// welding itself to the edges of the content region. Matches `bottom-3` /
// `right-3` in the template, and is added to any toolbar offset a page
// registers so the gap above the panel reads the same as the other three.
const PANEL_GAP = 12;
// Allow the side peek to take up to 95% of the CONTENT REGION it is positioned
// inside — not of the window, which is wider by the sidebar. Below that ceiling
// the panel honours the persisted width; above it, the displayed width is
// clamped so the panel never overflows its container and clips its own
// content on the left.
const MAX_WIDTH_RATIO = 0.95;

const panelWidth = ref(DEFAULT_WIDTH);

function loadWidth() {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
        const parsed = Number(stored);
        if (!Number.isNaN(parsed) && parsed >= MIN_WIDTH) {
            panelWidth.value = parsed;
        }
    }
}
loadWidth();

// Reactively track viewport width so the rendered panel width clamps in
// real time as the window resizes. The persisted `panelWidth` is left
// untouched so a desktop-stored wide value restores cleanly when the user
// returns to a wide screen.
const viewportWidth = ref(typeof window === 'undefined' ? 1024 : window.innerWidth);
function onViewportResize() {
    viewportWidth.value = window.innerWidth;
}

// The panel is absolutely positioned inside the content region, which is
// narrower than the window by the sidebar — and narrows again when the sidebar
// expands, without any window resize to notice. Measuring the containing block
// is what makes the ceiling the thing that actually stops the panel: measured
// against the window it sat ABOVE the CSS `max-w-[calc(100%-1.5rem)]`, so the
// max-width did the clamping while `startWidth` seeded the drag from the
// uncapped number — the dead zone the resize baseline exists to avoid.
const containerWidth = ref(0);
useResizeObserver(
    () => panelRef.value?.parentElement ?? null,
    (entries) => {
        containerWidth.value = entries[0]?.contentRect.width ?? 0;
    },
);

function viewportCeiling(): number {
    // Fall back to the window only before the observer has measured (first
    // paint, and jsdom, where layout boxes are all zero).
    const available = containerWidth.value > 0 ? containerWidth.value : viewportWidth.value;
    // The floating panel also gives up its side gaps, so the ceiling is the
    // ratio minus both of them.
    return Math.max(MIN_WIDTH, available * MAX_WIDTH_RATIO - PANEL_GAP * 2);
}

const effectivePanelWidth = computed(() =>
    Math.min(panelWidth.value, viewportCeiling()),
);

const isResizing = ref(false);
const startX = ref(0);
const startWidth = ref(0);

function onResizeStart(event: PointerEvent) {
    isResizing.value = true;
    startX.value = event.clientX;
    // Use the *rendered* width as the drag baseline. If we used the
    // persisted `panelWidth` (which may exceed the current viewport), a
    // user dragging inward would see a long dead zone before the panel
    // started shrinking — the source of the snapping/glitching.
    startWidth.value = effectivePanelWidth.value;
    document.addEventListener('pointermove', onResizeMove);
    document.addEventListener('pointerup', onResizeEnd);
}

function onResizeMove(event: PointerEvent) {
    if (!isResizing.value) return;
    const ceiling = viewportCeiling();
    const delta = startX.value - event.clientX;
    panelWidth.value = Math.min(ceiling, Math.max(MIN_WIDTH, startWidth.value + delta));
}

function onResizeEnd() {
    if (!isResizing.value) return;
    isResizing.value = false;
    localStorage.setItem(STORAGE_KEY, String(Math.round(panelWidth.value)));
    document.removeEventListener('pointermove', onResizeMove);
    document.removeEventListener('pointerup', onResizeEnd);
}

function onKeydown(event: KeyboardEvent) {
    if (event.key !== 'Escape' || !inspectorStore.isOpen || isPhone.value) return;
    // A dialog, menu or dropdown is open on top (reka-ui marks each open layer).
    // That Escape is the layer's to handle: reka closes the top layer only. This
    // listener runs first, so without this check Escape in the schedule editor's
    // hour dropdown closed the whole inspector and threw the edit away. In center
    // peek the inspector is itself a reka dialog, and reka closes it on Escape.
    if (document.querySelector('[data-dismissable-layer]')) return;
    // Escape closes the whole stack of peeks, as × does. Back steps one at a time.
    // Marked as handled, so the list underneath does not also take it.
    event.preventDefault();
    inspectorStore.close();
}

onMounted(() => {
    document.addEventListener('keydown', onKeydown);
    window.addEventListener('resize', onViewportResize);
});
onBeforeUnmount(() => {
    document.removeEventListener('keydown', onKeydown);
    window.removeEventListener('resize', onViewportResize);
});

watchEffect(() => {
    if (isResizing.value) {
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
    } else {
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
    }
});
</script>

<template>
    <!--
      The panel is inset from the right edge, so sliding it out by exactly its
      own width would leave a sliver parked in the gap. Travel the extra
      `PANEL_GAP` (0.75rem) so it clears the edge completely.
    -->
    <Transition
        enter-active-class="transition-transform duration-300 ease-out"
        leave-active-class="transition-transform duration-200 ease-in"
        enter-from-class="translate-x-[calc(100%+0.75rem)]"
        enter-to-class="translate-x-0"
        leave-from-class="translate-x-0"
        leave-to-class="translate-x-[calc(100%+0.75rem)]"
    >
        <div
            v-if="!isPhone && inspectorStore.isOpen && inspectorStore.component"
            ref="panelRef"
            data-testid="inspector-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="inspector-panel-title"
            class="elevated-surface absolute bottom-3 right-3 z-40 flex flex-col overflow-hidden rounded-3xl max-w-[calc(100%-1.5rem)]"
            :style="{
                width: `${effectivePanelWidth}px`,
                top: `${inspectorStore.topOffset + PANEL_GAP}px`,
            }"
        >
            <!-- Resize handle. Rounded to sit flush inside the sheet's left corners. -->
            <div
                class="absolute inset-y-0 left-0 w-2 cursor-col-resize rounded-l-3xl hover:bg-primary/20 active:bg-primary/30 transition-colors z-10"
                @pointerdown.prevent="onResizeStart"
            />

            <!-- The shared peek header: back and forward, the trail, the ⋯ menu,
                 the full page and close. -->
            <PeekHeader />

            <!--
              Body. Audio playback is handled by the single global
              <AudioPlayer> mounted in SidebarLayout — the inspector no
              longer mounts its own, so there is never more than one
              <audio> element competing for the player store.
            -->
            <!-- One surface from edge to edge, as the dialogs. -->
            <div class="flex min-h-0 flex-1 flex-col overflow-hidden">
                <div class="min-h-0 flex-1 overflow-auto px-4 pt-2 pb-6 sm:px-6 sm:pt-2 sm:pb-10">
                    <!-- The dialog's accessible name; the visible title below may be a
                         view-supplied component (live content, e.g. a player status line). -->
                    <h3 id="inspector-panel-title" class="sr-only">
                        {{ inspectorStore.title
                        }}<template v-if="inspectorStore.subtitle">
                            · {{ inspectorStore.subtitle }}</template
                        >
                    </h3>
                    <div class="mb-4">
                        <component
                            :is="inspectorStore.headerComponent"
                            v-if="inspectorStore.headerComponent"
                            v-bind="{ ...inspectorStore.headerProps, size: 'title' }"
                        />
                        <template v-else>
                            <!-- Titles here are often long and machine-made (a
                                 generated playlist's filename), and the panel is
                                 draggable down to 300px — so the visible heading
                                 is frequently just the first third of the name.
                                 The tooltip is what gets you the rest, and it
                                 only appears when the text is actually clipped. -->
                            <OverflowTooltip
                                as="h2"
                                :deps="inspectorStore.title"
                                trigger-class="text-xl leading-tight font-semibold"
                                data-testid="inspector-panel-heading"
                            >
                                {{ inspectorStore.title }}
                            </OverflowTooltip>
                            <p
                                v-if="inspectorStore.subtitle"
                                class="text-muted-foreground mt-0.5 text-sm"
                            >
                                {{ inspectorStore.subtitle }}
                            </p>
                        </template>
                    </div>
                    <component
                        :is="inspectorStore.component"
                        v-bind="inspectorStore.componentProps"
                    />
                </div>
            </div>
        </div>
    </Transition>
</template>
